/**
 * NotificationItem — bildirim kutusu tek satır (N2-02)
 *
 * İMZA öğe: okunmamışta sol marka-renkli accent bar + hafif tint + sağ dot.
 * Okundukça `transition-colors duration-500` ile yumuşak solar. Metin GÜVENLİ:
 * title/body React text node olarak render edilir (otomatik escape) —
 * dangerouslySetInnerHTML KESİNLİKLE kullanılmaz (T-N2-03 stored-XSS mitigasyonu).
 *
 * Renk kararı (D-03b, ui-ux-pro-max araştırması): accent = brand-chartreuse.
 * Amber zemin + koyu-teal foreground kontrastı ~8.5:1 (WCAG AAA); purple (teal)
 * alternatifi beyaz metinle sadece ~2.8:1 (AA'yı geçemez). Okunmamış sinyali
 * yalnız renge değil çok-sinyale dayanır (accent bar + dot + kalın başlık) —
 * Inclusive Design "no color-only indicators" ilkesi.
 */
import { formatDistanceToNow } from 'date-fns'
import { tr } from 'date-fns/locale'
import { useState } from 'react'

import { cn } from '@/lib/utils'
import type { NotificationItem as NotificationItemType } from '@/services/notifications-api'

interface NotificationItemProps {
  notification: NotificationItemType
  onRead: (id: string) => void
}

/** ISO string → "3 saat önce" (date-fns tr locale, addSuffix). Hatalıda boş döner. */
function formatRelativeTime(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return formatDistanceToNow(date, { addSuffix: true, locale: tr })
}

export function NotificationItem({ notification, onRead }: NotificationItemProps) {
  const isUnread = !notification.is_read
  const relativeTime = formatRelativeTime(notification.created_at)

  // Uzun bildirim gövdesi varsayılan 2 satıra kırpılır; tıklama tam metni açar/kapar
  // (#349). Hover için ayrıca native `title` tooltip'i tam metni gösterir.
  const [expanded, setExpanded] = useState(false)

  const handleActivate = () => {
    // Yalnız okunmamışken işaretle; okunmuşta no-op (gereksiz mutasyon yok).
    if (isUnread) onRead(notification.id)
    // Tam metni aç/kapa — okundu durumundan bağımsız (okunmuş bildirim de açılabilir).
    setExpanded((prev) => !prev)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleActivate()
    }
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleActivate}
      onKeyDown={handleKeyDown}
      aria-label={notification.title}
      aria-expanded={expanded}
      data-unread={isUnread ? 'true' : 'false'}
      className={cn(
        'relative flex cursor-pointer gap-3 px-4 py-3 outline-none transition-colors duration-500',
        'hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:ring-1 focus-visible:ring-ring',
        isUnread && 'bg-brand-chartreuse/[0.06]'
      )}
    >
      {/* Sol accent bar — yalnız okunmamışta (İMZA), okundukça DOM'dan kalkar */}
      {isUnread && (
        <span
          data-testid="notification-accent-bar"
          aria-hidden="true"
          className="pointer-events-none absolute left-0 top-0 h-full w-[3px] bg-brand-chartreuse"
        />
      )}

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p
          className={cn(
            'text-sm text-foreground',
            isUnread ? 'font-semibold' : 'font-medium'
          )}
        >
          {notification.title}
        </p>
        <p
          title={notification.body}
          className={cn(
            'whitespace-pre-line text-sm text-muted-foreground',
            !expanded && 'line-clamp-2'
          )}
        >
          {notification.body}
        </p>
        {relativeTime && (
          <span className="mt-0.5 text-xs text-muted-foreground">{relativeTime}</span>
        )}
      </div>

      {/* Sağ okunmamış dot — accent-bar'a ek net sinyal (çok-sinyal, renk-tek-başına-değil) */}
      {isUnread && (
        <span
          data-testid="notification-unread-dot"
          aria-hidden="true"
          className="mt-1.5 size-2 shrink-0 rounded-full bg-brand-chartreuse"
        />
      )}
    </div>
  )
}
